'use client';

import { useRouter } from 'next/navigation';
import { useCheckUser } from '@/hooks/useUser';
import { useCompanyEvent } from '@/hooks/useEvent';
import { useEventStore } from '@/store/eventStore';
import { useLineProfile } from '@/hooks/useLine';
import { useEffect } from 'react';

export default function Home() {
  const router = useRouter();
  const { lineProfile, authState, error, retry } = useLineProfile();
  const lineId = lineProfile.userId;
  
  const { currentEvent } = useEventStore();
  
  // Fetch company event first
  const { data: event, isLoading: isEventLoading, error: eventError } = useCompanyEvent();
  
  // Only check user if we have an event and lineId  
  const { data: user, isLoading: isUserLoading } = useCheckUser(
    event && lineId ? lineId : null
  );


  useEffect(() => {
    // Allow both authenticated and partial states to proceed
    if ((authState !== 'authenticated' && authState !== 'partial') || !lineId || isEventLoading) return;
    
    // If no event available, show alert and don't proceed
    if (!(event && event.RECORD_ID)) {
      alert('⚠️ No active events found. This application requires an active event to proceed. Please contact support.');
      return;
    }
    
    // Proceed with user check only if we have an event
    console.log('Event:', {
      lineId, event, currentEvent, user, isEventLoading, isUserLoading
    });
    if (!isUserLoading) {
      if (user) {
        // if user have mobile and mobile number is verified
        if (user.mobile && user.verifiedMobile) {
          router.push(`/welcome-back?lineId=${lineId}`);
        } else {
          router.push(`/register?lineId=${lineId}`);
        }
      } else {
        router.push(`/register?lineId=${lineId}`);
      }
    }
  }, [authState, lineId, event, user, isEventLoading, isUserLoading, router]);

  // Show loading state during authentication
  if (authState === 'loading' || authState === 'authenticating') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {authState === 'loading' ? 'Loading Line profile...' : 'Authenticating with Line...'}
          </p>
        </div>
      </div>
    );
  }


  // Show error state with retry option
  if (authState === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">
                {error?.includes('profile') ? 'Profile Access Error' : 'Need Authorization'}
              </h1>
              <p className="text-gray-600 mb-4">
                {error?.includes('profile') 
                  ? 'Unable to access your Line profile information. This may be due to privacy settings or temporary connectivity issues.'
                  : 'Authorize with Line. Please make sure you\'re accessing this from the Line app.'
                }
              </p>
              <button 
                onClick={retry}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-colors"
              >
                Authorize
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading state for other operations
  if (isEventLoading || isUserLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isEventLoading ? 'Loading event information...' : 'Checking your account...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error state for event loading
  if (eventError && !event) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 to-orange-100">
        <div className="max-w-md w-full mx-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-3xl font-bold text-gray-800 mb-2">No Active Events</h1>
              <p className="text-gray-600">
                This application requires an active event to proceed. Please contact support.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
